import { Field, Select } from "components/shared/form";
import Loader from "components/shared/loader";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { featureModuleTypesEnum, roles, rolesEnum, userStatesEnum } from "helpers/enums";
import debounce from "lodash/debounce";
import { ITEM_ROOT } from "odata-query";
import React, { Component } from "react";
import { ban, getInvitationsList, getList, removeInvite, unban } from "sources/profile/participants";
import { Participant } from "./participant";

const truckingOrgRolesForRoot = [
    rolesEnum.TruckingOrganizationAdmin,
    rolesEnum.TruckingOrganizationManager,
    rolesEnum.TruckingOrganizationHeadLogist,
    rolesEnum.TruckingOrganizationOperator,
    rolesEnum.TruckingOrganizationViewer,
    rolesEnum.TruckingOrganizationExtendedViewer,
    rolesEnum.IntegrationAccount,
    rolesEnum.IntegrationManager,
    rolesEnum.TruckingOrganizationDispatcher,
    rolesEnum.AccountManager,
    rolesEnum.TechSupport,
];

const truckingOrgRolesForAdmin = [
    rolesEnum.TruckingOrganizationAdmin,
    rolesEnum.TruckingOrganizationManager,
    rolesEnum.TruckingOrganizationHeadLogist,
    rolesEnum.TruckingOrganizationOperator,
    rolesEnum.TruckingOrganizationViewer,
    rolesEnum.TruckingOrganizationExtendedViewer,
    rolesEnum.IntegrationAccount,
    rolesEnum.TruckingOrganizationDispatcher,
];

const truckingOrgRolesForCrStart = [
    rolesEnum.TruckingOrganizationAdmin,
    rolesEnum.TruckingOrganizationManager,
    rolesEnum.TruckingOrganizationHeadLogist,
    rolesEnum.IntegrationAccount,
];

const truckingOrgRolesForIntegration = [rolesEnum.AccountManager, rolesEnum.TechSupport];

const defaultRoleFilter = [
    { not: { roleTypes: { any: [] } } },
    { roleTypes: { any: { [ITEM_ROOT]: { ne: rolesEnum.Driver } } } },
];

export default class OrgMembers extends Component {
    state = {
        isLoading: true,
        items: [],
        filters: {
            searchStr: "",
            roles: null,
        },
    };

    constructor(props) {
        super(props);
        this.filterByName = debounce(this.filterByName, 500);
    }

    componentDidMount() {
        this.getItems();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.value !== nextProps.value) {
            this.getItems(nextProps);
        }
        if (nextProps.shouldUpdate) {
            nextProps.updateToggle(false);
            this.getItems(nextProps);
        }
    }

    getItems = (nextProps) => {
        this.setState({ isLoading: true });
        const { value } = nextProps || this.props;

        if (value === userStatesEnum.Invited) {
            return getInvitationsList({ filters: [{ dataIndex: "State", value }] }).then((items) =>
                this.setState({ isLoading: false, items }),
            );
        }

        const preparedFilter = this.buildPreparedFilters();

        return getList({ filters: [{ dataIndex: "State", value }], preparedFilter }).then((items) =>
            this.setState({ isLoading: false, items }),
        );
    };

    buildPreparedFilters = () => {
        const { filters } = this.state;
        const preparedFilter = { and: [] };
        if (filters.searchStr.length > 0) {
            preparedFilter.and.push({
                or: [
                    {
                        "tolower(email)": {
                            contains: {
                                type: "raw",
                                value: `'${encodeURIComponent(filters.searchStr.toLowerCase())}'`,
                            },
                        },
                    },
                    {
                        "tolower(fullName)": {
                            contains: {
                                type: "raw",
                                value: `'${encodeURIComponent(filters.searchStr.toLowerCase())}'`,
                            },
                        },
                    },
                ],
            });
        }
        if (filters.roles && filters.roles.length) {
            preparedFilter.roleTypes = { any: { [ITEM_ROOT]: { in: filters.roles } } };
        } else {
            preparedFilter.and.push({ or: defaultRoleFilter });
        }
        return preparedFilter;
    };

    delItem = (userId) => {
        const { modal } = this.props;
        const { action, title, text } = this.calcAction();
        return modal({
            name: "confirm",
            title: `${title} пользователя`,
            text: `Вы уверены что хотите ${text} пользователя?`,
            action: () => action({ userId }),
            onSuccessAction: () => this.getItems(this.props),
        });
    };

    calcAction = () => {
        const { action, invite } = this.props;
        if (invite) {
            return { action: removeInvite, title: "Приглашение", text: "отменить приглашение" };
        } else if (action === "ban") {
            return { action: ban, title: "Блокировка", text: "заблокировать" };
        } else if (action === "unban") {
            return { action: unban, title: "Разблокировка", text: "разблокировать" };
        }
    };

    onInputChange = ({ value }) => {
        this.setState({ filters: { ...this.state.filters, searchStr: value } });
        this.filterByName();
    };

    onSelectChange = ({ value }) => {
        this.setState({ filters: { ...this.state.filters, roles: value } }, this.getItems);
    };

    filterByName = () => {
        this.getItems();
    };

    renderList = () => {
        const { items, isLoading } = this.state;
        const {
            isAdmin,
            org,
            profile: { availableFeatureModules },
        } = this.props;
        const isCrStart = availableFeatureModules.includes(featureModuleTypesEnum.CrStart);
        let truckingOrgRoles = truckingOrgRolesForAdmin;
        if (this.props.roles) {
            const { isRoot, isIntegrationManager } = this.props.roles;
            truckingOrgRoles = isRoot
                ? truckingOrgRolesForRoot
                : isIntegrationManager
                  ? truckingOrgRolesForIntegration
                  : isCrStart
                    ? truckingOrgRolesForCrStart
                    : truckingOrgRolesForAdmin;
        }
        if (isLoading) {
            return <Loader prefix="margin-center" />;
        }
        const { value } = this.props;
        return items.length ? (
            items.map((item) => {
                const deletable = isAdmin && !item.roleTypes.includes(org);
                const props = {
                    imageUrl: item.imageUrl,
                    name: item.fullName,
                    email: item.email,
                    phone: item.phoneNumber,
                    roleTypes: item.roleTypes,
                    id: item.id,
                    orgId: this.props.org,
                    truckingOrgRoles,
                    hideRolesSelect: value === userStatesEnum.Invited,
                    ...(deletable ? { del: () => this.delItem(item.id) } : {}),
                };
                return <Participant key={item.id} {...props} />;
            })
        ) : (
            <div className="panel__empty-msg">Не найдено</div>
        );
    };

    createUser = () => {
        this.props.modal({
            name: "addUser",
            windowMod: "wide",
            title: "Создание пользователя",
        });
    };

    render() {
        const { value, profile } = this.props;
        let truckingOrgRoles = truckingOrgRolesForAdmin;
        if (this.props.roles) {
            const { isRoot, isIntegrationManager } = this.props.roles;
            const isCrStart = profile.availableFeatureModules.includes(featureModuleTypesEnum.CrStart);
            truckingOrgRoles = isRoot
                ? truckingOrgRolesForRoot
                : isIntegrationManager
                  ? truckingOrgRolesForIntegration
                  : isCrStart
                    ? truckingOrgRolesForCrStart
                    : truckingOrgRolesForAdmin;
        }
        return (
            <>
                {value !== userStatesEnum.Invited && (
                    <div className="d-inline-flex justify-content-between pb-3">
                        <div className="d-flex justify-content-between">
                            <div className="panel__filter-item pr-2">
                                <Field
                                    inputProps={{
                                        name: "search",
                                        placeholder: "Поиск по имени и email",
                                        className: "field-wrap__input",
                                    }}
                                    value={this.state.filters.searchStr}
                                    onChange={this.onInputChange}
                                    bWrapper="row"
                                />
                            </div>
                            <div className="panel__filter-item">
                                <Select
                                    {...defaultMultipleSelectProps}
                                    components={{ ValueContainer }}
                                    placeholder="Фильтр по ролям"
                                    inputProps={{ name: "search" }}
                                    value={this.state.filters.roles}
                                    options={roles.filter((item) => truckingOrgRoles.includes(item.value))}
                                    onChange={this.onSelectChange}
                                    bWrapper="row"
                                />
                            </div>
                        </div>
                        <div className="btn btn-sm btn-brand" onClick={this.createUser}>
                            Создать пользователя
                        </div>
                    </div>
                )}
                {this.renderList()}
            </>
        );
    }
}
